/**
 * Pure helpers for mesh-file imports (OBJ / glTF / FBX).
 *
 * Mesh-file imports are always packaged as collection assets so one source
 * file maps cleanly to one reusable grouped asset in the library and scene.
 */

import type { LibraryItemMetadata } from "@poseref/shared";
import type { ImportedMeshResult } from "../modeling/importTypes.ts";
import type { MaterialSlot, SceneObject } from "../modeling/types.ts";
import {
  createDefaultMaterialSlots,
  createMaterialId,
  createSceneObjectId,
} from "../modeling/types.ts";
import { buildCollectionFile } from "./libraryIO.ts";
import type { LibraryCollectionDocument } from "./persistence/types.ts";

export interface ImportedMeshLibraryBundle {
  itemName: string;
  sceneObjects: SceneObject[];
  fileData: LibraryCollectionDocument;
  metadata: LibraryItemMetadata;
}

function createImportedMaterialSlots(
  _result: ImportedMeshResult,
): MaterialSlot[] {
  // Future material import should branch here, consuming `result.materialHints`
  // to build one or more material slots without rewriting the import flow.
  return createDefaultMaterialSlots(createMaterialId());
}

/**
 * Convert normalized imported meshes into a collection-backed library asset.
 *
 * Even single-mesh files become a one-object collection so a file always
 * spawns into the scene as a manipulable grouped unit.
 */
export function buildImportedMeshLibraryBundle(
  baseName: string,
  sourceFormat: string,
  results: ImportedMeshResult[],
  createId: () => string = createSceneObjectId,
): ImportedMeshLibraryBundle {
  if (results.length === 0) {
    throw new Error("Cannot build a mesh import bundle with no meshes");
  }

  const sceneObjects = results.map((result) => ({
    id: createId(),
    name: results.length === 1 ? baseName : `${baseName}.${result.name}`,
    objectKind: "imported" as const,
    position: [0, 0, 0] as [number, number, number],
    rotation: [0, 0, 0] as [number, number, number],
    scale: [1, 1, 1] as [number, number, number],
    vertices: result.geometry.vertices,
    indices: result.geometry.indices,
    loopNormals: result.geometry.loopNormals,
    topology: result.geometry.topology,
    uvs: result.geometry.uvs,
    materialSlots: createImportedMaterialSlots(result),
    shading: "smooth" as const,
    imported: true,
  }));

  let vertexCount = 0;
  for (const obj of sceneObjects) vertexCount += obj.vertices.length / 3;

  return {
    itemName: baseName,
    sceneObjects,
    fileData: buildCollectionFile(sceneObjects),
    metadata: {
      vertexCount,
      objectCount: sceneObjects.length,
      objectNames: sceneObjects.map((object) => object.name),
      sourceFormat,
    },
  };
}
